import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { ContextMenu } from 'primereact/contextmenu'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { ProgressSpinner } from 'primereact/progressspinner'
import { classNames } from 'primereact/utils'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  PostProcImage,
  PostProcImageFilters,
  PostProcImageQueryParams,
  useGetPostProcImages
} from '../../api/submission-API'
import MetricMultiSelect from './MetricMultiSelect'

type PostProcGalleryProps = {
  systemRunId: string
  runId: string
  caseName: string
  onClose?: () => void
  isExpanded?: boolean
  /** Index to open in sync with the sibling gallery */
  syncedIndex?: number | null
  /** Called when this gallery navigates so the sibling can open the same index */
  onSyncedImageChange?: (index: number | null) => void
  /** Gallery index (0 or 1) when in paired mode */
  galleryIndex?: number
  /** Whether paired galleries are linked (scroll together) */
  galleriesLinked?: boolean
  /** Called when the user toggles link/unlink for paired galleries */
  onToggleLink?: () => void
  /** Called when user wants to fullscreen this gallery in paired mode */
  onRequestFullscreen?: (galleryIndex: number) => void
  /** Called when the current lightbox image changes */
  onCurrentImageChange?: (image: PostProcImage | null, allImages: PostProcImage[]) => void
  /** Baseline/ref case gm_id to include as the first case when opening in MAV */
  baselineRunId?: string
  /** Ref2 case gm_id to include as an additional comparison when opening in MAV */
  ref2RunId?: string
}

const BATCH_SIZE = 100
const lightboxNavButtonClass =
  'absolute top-1/2 -translate-y-1/2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/20 bg-black/55 p-0 text-white shadow-lg transition-colors hover:bg-black/75 focus:outline-none focus:ring-2 focus:ring-white/70'

/**
 * Full-featured image gallery with virtual scrolling, filtering, and comparison mode
 */
const PostProcGallery: FC<PostProcGalleryProps> = ({
  systemRunId,
  runId,
  caseName,
  onClose,
  isExpanded = true,
  syncedIndex,
  onSyncedImageChange,
  galleryIndex,
  galleriesLinked,
  onToggleLink,
  onRequestFullscreen,
  onCurrentImageChange,
  baselineRunId,
  ref2RunId
}) => {
  const isPaired = galleryIndex !== undefined
  const [{ data, loading, error }, fetchImages] = useGetPostProcImages()
  // Second hook instance — used only to fetch available filter options on first load
  const [{ data: filterSeedData }, fetchFiltersOnly] = useGetPostProcImages()

  // Filter state — default to Surface so the initial view is focused
  const [selectedImageType, setSelectedImageType] = useState<string | null>('Surface')
  const [selectedSection, setSelectedSection] = useState<string | null>(null)
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null)
  const [selectedMetricTypes, setSelectedMetricTypes] = useState<string[]>([])

  // Pagination state
  const [allImages, setAllImages] = useState<PostProcImage[]>([])
  const contextMenuRef = useRef<ContextMenu>(null)
  const [contextMenuImage, setContextMenuImage] = useState<PostProcImage | null>(null)
  const contextMenuModel = useMemo(
    () => [
      {
        label: 'Open in MAV',
        icon: 'pi pi-external-link',
        command: () => {
          if (contextMenuImage) handleOpenInMav(contextMenuImage)
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contextMenuImage]
  )
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  // Sentinel ref for IntersectionObserver-based infinite scroll
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const hasLoadedRef = useRef(false)

  // Filter options seeded from an unfiltered request so all types are always shown
  const [originalFilters, setOriginalFilters] = useState<PostProcImageFilters | null>(null)

  // Lightbox state
  const [lightboxImage, setLightboxImage] = useState<PostProcImage | null>(null)

  // Auto-play state
  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackSpeed, setPlaybackSpeed] = useState(1) // 1x speed
  const [loopEnabled, setLoopEnabled] = useState(true)

  // Comparison mode state
  const [comparisonMode, setComparisonMode] = useState(false)
  const [selectedForComparison, setSelectedForComparison] = useState<PostProcImage[]>([])
  const [showComparisonDialog, setShowComparisonDialog] = useState(false)

  // The metric dropdown is multi-select and encodes both the metric and (for compare deltas) the
  // reference case:
  //   []              -> All Metrics
  //   "dCp"           -> plain baseline metric
  //   "dCp||323"      -> compare delta of dCp against reference case 323
  // An empty selection is restricted to baseline images (referenceRunId='none') so compare deltas
  // only appear when explicitly chosen.
  const { metricFilterParams, referenceFilterParam } = useMemo(() => {
    if (!selectedMetricTypes.length) {
      return {
        metricFilterParams: undefined as string[] | undefined,
        referenceFilterParam: 'none'
      }
    }
    return { metricFilterParams: selectedMetricTypes, referenceFilterParam: undefined }
  }, [selectedMetricTypes])

  // Seed available filter options once per systemRunId by fetching 1 image with no type filter.
  // This ensures the Image Type dropdown always shows ALL types even when Surface is the default.
  useEffect(() => {
    if (!isExpanded) return
    fetchFiltersOnly(systemRunId, { skip: 0, limit: 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemRunId, isExpanded])

  // Apply filter seed data to originalFilters
  useEffect(() => {
    if (filterSeedData?.availableFilters && !originalFilters) {
      setOriginalFilters(filterSeedData.availableFilters)
    }
  }, [filterSeedData, originalFilters])

  // Initial load and filter changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isExpanded) return

    // Reset when filters change
    setAllImages([])
    setHasMore(true)
    setIsLoadingMore(false)
    setLightboxImage(null) // Clear selected image to return to grid view
    onSyncedImageChange?.(null) // Clear sync to prevent misalignment between galleries

    const params: PostProcImageQueryParams = {
      skip: 0,
      limit: BATCH_SIZE,
      imageType: selectedImageType || undefined,
      section: selectedSection || undefined,
      location: selectedLocation || undefined,
      metricFilters: metricFilterParams,
      referenceRunId: referenceFilterParam
    }
    fetchImages(systemRunId, params)
  }, [
    systemRunId,
    selectedImageType,
    selectedSection,
    selectedLocation,
    selectedMetricTypes,
    isExpanded
  ])

  // Handle data updates — append paginated results
  useEffect(() => {
    if (data?.images) {
      hasLoadedRef.current = true
      if (data.skip === 0) {
        setAllImages(data.images)
        // Don't auto-select image - let user click to view
        // But notify parent about available images for fullscreen coordination
        if (isPaired && data.images.length > 0) {
          onCurrentImageChange?.(null, data.images)
        }
      } else {
        setAllImages(prev => [...prev, ...data.images])
      }
      setHasMore(data.hasMore)
      setIsLoadingMore(false)
    }
  }, [data, isPaired, onCurrentImageChange])

  // Load next page
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || loading) return
    setIsLoadingMore(true)
    const params: PostProcImageQueryParams = {
      skip: allImages.length,
      limit: BATCH_SIZE,
      imageType: selectedImageType || undefined,
      section: selectedSection || undefined,
      location: selectedLocation || undefined,
      metricFilters: metricFilterParams,
      referenceRunId: referenceFilterParam
    }
    fetchImages(systemRunId, params)
  }, [
    isLoadingMore,
    hasMore,
    loading,
    allImages.length,
    selectedImageType,
    selectedSection,
    selectedLocation,
    selectedMetricTypes,
    fetchImages,
    systemRunId
  ])

  // IntersectionObserver on sentinel element to trigger loadMore
  useEffect(() => {
    const sentinel = sentinelRef.current
    const container = scrollContainerRef.current
    if (!sentinel || !container) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      { root: container, rootMargin: '400px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [loadMore])

  // Notify parent of current image changes (for fullscreen coordination)
  useEffect(() => {
    if (isPaired && onCurrentImageChange) {
      onCurrentImageChange(lightboxImage, allImages)
    }
  }, [isPaired, lightboxImage, allImages, onCurrentImageChange])

  // Build filter options - use preserved original filters so options stay stable
  const imageTypeOptions = useMemo(() => {
    const types = originalFilters?.imageTypes || data?.availableFilters?.imageTypes || []
    return [
      { label: 'All Types', value: null },
      ...types.map(t => ({ label: t === 'Sect' ? 'Section' : t, value: t }))
    ]
  }, [originalFilters?.imageTypes, data?.availableFilters?.imageTypes])

  const sectionOptions = useMemo(() => {
    const sections = originalFilters?.sections || data?.availableFilters?.sections || []
    return [{ label: 'All Sect.', value: null }, ...sections.map(s => ({ label: s, value: s }))]
  }, [originalFilters?.sections, data?.availableFilters?.sections])

  const locationOptions = useMemo(() => {
    const locations = originalFilters?.locations || data?.availableFilters?.locations || []
    return [{ label: 'All Loc.', value: null }, ...locations.map(l => ({ label: l, value: l }))]
  }, [originalFilters?.locations, data?.availableFilters?.locations])

  const metricTypeOptions = useMemo(() => {
    const types = originalFilters?.metricTypes || data?.availableFilters?.metricTypes || []
    // Compare deltas (from compare_pictures) against additional reference cases, e.g. "dCp (323)".
    // Value is encoded as "{metric}||{referenceRunId}" so the fetch can filter on both.
    const referenceMetrics =
      originalFilters?.referenceMetrics || data?.availableFilters?.referenceMetrics || []
    const compareOptions = referenceMetrics.map(rm => ({
      label: `${rm.metricType} (${rm.referenceRunId})`,
      value: `${rm.metricType}||${rm.referenceRunId}`
    }))
    return [...types.map(t => ({ label: t, value: t })), ...compareOptions]
  }, [
    originalFilters?.metricTypes,
    originalFilters?.referenceMetrics,
    data?.availableFilters?.metricTypes,
    data?.availableFilters?.referenceMetrics
  ])

  // Use allImages directly - filtering is now done server-side
  const filteredImages = allImages

  // Toggle comparison selection — stable callback using refs
  const toggleComparison = useCallback((image: PostProcImage) => {
    setSelectedForComparison(prev => {
      const isSelected = prev.some(img => img.fullPath === image.fullPath)
      if (isSelected) {
        return prev.filter(img => img.fullPath !== image.fullPath)
      }
      // Limit to 4 images for comparison
      if (prev.length >= 4) {
        return [...prev.slice(1), image]
      }
      return [...prev, image]
    })
  }, [])

  // Navigate in lightbox
  const navigateLightbox = (direction: 'prev' | 'next') => {
    if (!lightboxImage) return
    const currentIndex = allImages.findIndex(img => img.fullPath === lightboxImage.fullPath)
    if (currentIndex === -1) return

    let newIndex: number
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : allImages.length - 1
    } else {
      newIndex = currentIndex < allImages.length - 1 ? currentIndex + 1 : 0
    }

    // Stop at end if loop is disabled
    if (!loopEnabled && direction === 'next' && currentIndex === allImages.length - 1) {
      setIsPlaying(false)
      return
    }

    setLightboxImage(allImages[newIndex])
    // Only propagate index to sibling when galleries are linked
    if (galleriesLinked !== false) {
      onSyncedImageChange?.(newIndex)
    }
  }

  // Auto-play effect
  useEffect(() => {
    if (!isPlaying || !lightboxImage) return

    const intervalMs = 1000 / playbackSpeed // Base interval is 1 second, adjusted by speed
    const timer = setInterval(() => {
      navigateLightbox('next')
    }, intervalMs)

    return () => clearInterval(timer)
  }, [isPlaying, lightboxImage, playbackSpeed, allImages, loopEnabled])

  // Stop playing when lightbox closes
  useEffect(() => {
    if (!lightboxImage) {
      setIsPlaying(false)
    }
  }, [lightboxImage])

  // Sync lightbox when sibling gallery navigates — match by index position
  useEffect(() => {
    if (syncedIndex === null || syncedIndex === undefined || allImages.length === 0) return
    const match = allImages[syncedIndex]
    if (match && match.fullPath !== lightboxImage?.fullPath) {
      setLightboxImage(match)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncedIndex, allImages])

  // Keyboard navigation for lightbox (left/right arrow keys)
  useEffect(() => {
    if (!lightboxImage) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        navigateLightbox('prev')
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        navigateLightbox('next')
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setLightboxImage(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxImage, allImages])

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // Maps post-proc surface location names to MAV camera view presets
  const LOCATION_TO_MAV_VIEW: Record<string, string> = {
    Top: 'Top',
    Bottom: 'Bottom',
    Left: 'Left',
    Right: 'Right',
    Front: 'Front',
    Aft: 'Back',
    BottomDiffuser: 'Bottom',
    BottomSplitter: 'Bottom'
  }

  const isDeltaImage = (image: PostProcImage): boolean => {
    const metricType = image.metadata.metricType?.toLowerCase() || ''
    const fullPath = image.fullPath.toLowerCase()

    return (
      fullPath.includes('/compare_pictures/') ||
      metricType.startsWith('d') ||
      metricType.includes('delta') ||
      metricType.includes('dcp') ||
      metricType.includes('dwss')
    )
  }

  const buildMavUrl = (image: PostProcImage): string | null => {
    try {
      // Extract raceTeamId and runId from image.fullPath
      // Format: {raceTeamId}/cases/{runId}/Results/post_proc/pictures/...
      const pathParts = image.fullPath.split('/')
      const casesIndex = pathParts.indexOf('cases')
      if (casesIndex < 1) return null
      const raceTeamId = pathParts.slice(0, casesIndex).join('/')
      const runId = pathParts[casesIndex + 1]
      if (!raceTeamId || !runId) return null

      const storageAccount = new URL(image.url).hostname.split('.')[0]
      if (!storageAccount) return null

      const storageContainer = 'cfd-cases'
      const dataPath = `https://${storageAccount}.blob.core.windows.net/${storageContainer}/${raceTeamId}/cases/${runId}/Results/MAV_${runId}`

      const isSection = image.metadata.imageType === 'Sect'

      // Build Cases array — if a baseline ref case exists, it goes first (same convention as the MAV web launcher)
      const cases: Array<Record<string, string>> = []
      if (baselineRunId && String(baselineRunId) !== String(runId)) {
        const baselineDataPath = `https://${storageAccount}.blob.core.windows.net/${storageContainer}/${raceTeamId}/cases/${baselineRunId}/Results/MAV_${baselineRunId}`
        cases.push({
          ID: String(baselineRunId),
          Name: 'Baseline',
          StorageAccount: storageAccount,
          StorageContainer: storageContainer,
          DataPath: baselineDataPath
        })
      }
      const hasBaseline = baselineRunId && String(baselineRunId) !== String(runId)
      cases.push({
        ID: runId,
        Name: hasBaseline ? 'Comparison' : caseName,
        StorageAccount: storageAccount,
        StorageContainer: storageContainer,
        DataPath: dataPath
      })

      // Add ref2 as an additional comparison case if set and different from both baseline and current
      if (
        ref2RunId &&
        String(ref2RunId) !== String(runId) &&
        String(ref2RunId) !== String(baselineRunId || '')
      ) {
        const ref2DataPath = `https://${storageAccount}.blob.core.windows.net/${storageContainer}/${raceTeamId}/cases/${ref2RunId}/Results/MAV_${ref2RunId}`
        cases.push({
          ID: String(ref2RunId),
          Name: 'Comparison 2',
          StorageAccount: storageAccount,
          StorageContainer: storageContainer,
          DataPath: ref2DataPath
        })
      }

      const allIds = cases.map(c => c.ID)
      const sessionName = hasBaseline ? `Session ${allIds.join('-')}` : `Session ${runId}`
      const sessionDesc = hasBaseline
        ? `Comparing ${allIds.slice(1).join(', ')} against baseline ${baselineRunId}`
        : caseName

      const payload: Record<string, unknown> = {
        Name: sessionName,
        Description: sessionDesc,
        LocalStoragePath: '',
        NetworkStoragePath: '',
        Cases: cases
      }

      const sliceAxis =
        isSection && image.metadata.section ? image.metadata.section.split('-')[0] : null
      if (sliceAxis) payload.SliceAxis = sliceAxis
      if (isSection && image.metadata.metricType) payload.SliceVariable = image.metadata.metricType
      if (!isSection && image.metadata.metricType)
        payload.SurfaceVariable = image.metadata.metricType
      if (isSection && image.metadata.value != null)
        payload.SlicePosition = image.metadata.value * 0.0254
      if (isDeltaImage(image)) payload.IsDelta = true
      if (!isSection && image.metadata.location) {
        const mavView = LOCATION_TO_MAV_VIEW[image.metadata.location]
        if (mavView) payload.View = mavView
      }

      return `mav-launcher://${encodeURIComponent(JSON.stringify(payload))}`
    } catch {
      return null
    }
  }

  const handleOpenInMav = (image: PostProcImage) => {
    const url = buildMavUrl(image)
    if (!url) {
      toast.error('Unable to build MAV launch URL for this image.')
      return
    }
    console.log('[Open in MAV] raw JSON:', url.replace('mav-launcher://', ''))
    console.log(
      '[Open in MAV] decoded payload:',
      decodeURIComponent(url.replace('mav-launcher://', ''))
    )
    console.log('[Open in MAV] full URL:', url)
    window.open(url, '_blank')
  }

  // Full-page spinner is only for the very first load; later filter changes keep the header
  // (and any open filter panel) mounted.
  if (loading && allImages.length === 0 && !hasLoadedRef.current) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <ProgressSpinner style={{ width: '50px', height: '50px' }} />
        <p className="mt-4 text-gray-600 dark:text-gray-400">Loading images...</p>
      </div>
    )
  }

  // Error state - only show full error state if there's truly an error (not just empty filter results)
  if (error && allImages.length === 0 && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <i className="pi pi-exclamation-triangle text-4xl text-yellow-500 mb-4" />
        <p className="text-red-500 font-medium">Unable to load images</p>
        <p className="text-sm text-gray-500 mt-2">Please try again later.</p>
        <Button
          label="Retry"
          className="mt-4"
          outlined
          onClick={() => {
            setAllImages([])
            fetchImages(systemRunId, { skip: 0, limit: BATCH_SIZE })
          }}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with filters and controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4 px-2 py-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        {/* Left side: Title and counts */}
        <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
          <div className="flex flex-col items-stretch ">
            <h3 className="text-lg font-semibold text-neutral-80 dark:text-neutral-20 truncate">
              {caseName}
            </h3>
            <div className="text-sm text-neutral-60 dark:text-neutral-60">{runId}</div>
          </div>
          {loading && <ProgressSpinner style={{ width: '16px', height: '16px' }} />}
        </div>

        {/* Right side: Filters and actions */}
        <div className="flex flex-wrap items-center justify-end gap-1">
          {/* Image type filter */}
          <Dropdown
            value={selectedImageType}
            options={imageTypeOptions}
            optionLabel="label"
            optionValue="value"
            onChange={e => {
              setSelectedImageType(e.value)
              // When changing image type, always reset dependent filters
              setSelectedSection(null)
              setSelectedLocation(null)
              setSelectedMetricTypes([])
            }}
            placeholder="Type"
            className="text-xs w-36"
            pt={{ input: { className: '!py-2' } }}
          />

          {/* Section filter (only show for Sect type) */}
          {selectedImageType === 'Sect' && (
            <Dropdown
              value={selectedSection}
              options={sectionOptions}
              optionLabel="label"
              optionValue="value"
              onChange={e => setSelectedSection(e.value)}
              placeholder="Section"
              className="text-xs w-36"
              pt={{ input: { className: '!py-2' } }}
            />
          )}

          {/* Location filter (only show for Surface type) */}
          {selectedImageType === 'Surface' && (
            <Dropdown
              value={selectedLocation}
              options={locationOptions}
              optionLabel="label"
              optionValue="value"
              onChange={e => setSelectedLocation(e.value)}
              placeholder="Location"
              className="text-xs w-36"
              pt={{ input: { className: '!py-2' } }}
            />
          )}

          {/* Metric type filter (multi-select) */}
          <MetricMultiSelect
            value={selectedMetricTypes}
            options={metricTypeOptions}
            onChange={setSelectedMetricTypes}
            className="text-xs !w-28"
            placeholder="Metrics"
            compact
          />

          {/* Close button */}
          {onClose && (
            <Button
              icon="pi pi-times"
              onClick={onClose}
              severity="secondary"
              text
              rounded
              className="p-button-sm"
              style={{ padding: '0.3rem' }}
              tooltip="Close gallery"
              tooltipOptions={{ position: 'bottom' }}
            />
          )}

          {/* Link/Unlink button - only in paired mode */}
          {isPaired && onToggleLink && (
            <Button
              icon={galleriesLinked ? 'pi pi-lock' : 'pi pi-lock-open'}
              onClick={onToggleLink}
              severity="secondary"
              text
              rounded
              className="p-button-sm"
              style={{ padding: '0.3rem' }}
              tooltip={
                galleriesLinked
                  ? 'Unlink galleries (scroll independently)'
                  : 'Link galleries (scroll together)'
              }
              tooltipOptions={{ position: 'bottom' }}
            />
          )}

          {/* Fullscreen button */}
          { onRequestFullscreen && (
            <Button
              icon="pi pi-window-maximize"
              rounded
              style={{ padding: '0.3rem' }}
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()

                // Always ensure we have an image selected and parent is synced
                const imageToShow = lightboxImage || (allImages.length > 0 ? allImages[0] : null)

                if (imageToShow) {
                  // Update local state if needed
                  if (!lightboxImage) {
                    setLightboxImage(imageToShow)
                    onSyncedImageChange?.(0)
                  }

                  // Always sync with parent before opening fullscreen
                  onCurrentImageChange?.(imageToShow, allImages)

                  // Small delay to ensure React has processed the state updates
                  setTimeout(() => {
                    onRequestFullscreen(galleryIndex ?? 0)
                  }, 100)
                }
              }}
              severity="secondary"
              text
              className="p-button-sm"
              tooltip="Fullscreen"
              tooltipOptions={{ position: 'bottom' }}
            />
          )}
        </div>
      </div>

      {/* Image grid — hidden in paired mode when an image is open */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-auto"
        style={{ minHeight: 0, display: isPaired && lightboxImage ? 'none' : undefined }}>
        {loading && filteredImages.length === 0 ? (
          <div className="flex items-center justify-center h-full py-12">
            <ProgressSpinner style={{ width: '40px', height: '40px' }} />
          </div>
        ) : filteredImages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-12">
            <i className="pi pi-images text-4xl text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {data?.totalCount && data.totalCount > 0
                ? 'No images match the selected filters'
                : 'No post-processing images available'}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              {data?.totalCount && data.totalCount > 0
                ? 'Try adjusting your filters to see more images.'
                : 'Images will appear here once post-processing completes.'}
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 p-2" style={{ scrollbarGutter: 'stable' }}>
            {filteredImages.map(image => {
              const isSelectedForCompare = selectedForComparison.some(
                img => img.fullPath === image.fullPath
              )
              return (
                <div
                  key={image.fullPath}
                  className={`relative group cursor-pointer rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 border-2 transition-all ${
                    isSelectedForCompare
                      ? 'border-blue-500 ring-2 ring-blue-300'
                      : 'border-transparent hover:border-gray-300'
                  }`}
                  style={{ width: 120, height: 120, flexShrink: 0 }}
                  onClick={() => {
                    if (comparisonMode) {
                      toggleComparison(image)
                    } else {
                      const idx = allImages.findIndex(img => img.fullPath === image.fullPath)
                      setLightboxImage(image)
                      if (galleriesLinked !== false) {
                        onSyncedImageChange?.(idx)
                      }
                    }
                  }}
                  onContextMenu={e => {
                    setContextMenuImage(image)
                    contextMenuRef.current?.show(e)
                  }}>
                  {comparisonMode && (
                    <div className="absolute top-2 left-2 z-10">
                      <Checkbox
                        checked={isSelectedForCompare}
                        onChange={() => toggleComparison(image)}
                        className="bg-white rounded shadow"
                      />
                    </div>
                  )}
                  <img
                    src={image.url}
                    alt={image.name}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-end">
                    <div className="w-full p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-white text-xs truncate">{image.name}</p>
                      {image.metadata.imageType && (
                        <p className="text-white/70 text-xs">
                          {image.metadata.imageType === 'Sect'
                            ? `${image.metadata.section} | ${image.metadata.metricType} | #${image.metadata.index}`
                            : `${image.metadata.location} | ${image.metadata.metricType}`}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
            {/* Sentinel for infinite scroll */}
            <div ref={sentinelRef} style={{ width: '100%', height: 1 }} />
            {isLoadingMore && (
              <div className="w-full flex justify-center py-4">
                <ProgressSpinner style={{ width: '30px', height: '30px' }} />
              </div>
            )}
          </div>
        )}
      </div>

      <ContextMenu ref={contextMenuRef} model={contextMenuModel} />

      {/* Inline viewer — used in paired (side-by-side) mode so both panels show simultaneously */}
      {isPaired && lightboxImage && (
        <div
          className="flex-1 flex flex-col overflow-auto bg-white dark:bg-gray-900 p-4"
          style={{ minHeight: 0 }}>
          <div className="relative flex-1 w-full flex items-center justify-center min-h-0">
            <img
              src={lightboxImage.url}
              alt={lightboxImage.name}
              className="max-w-full max-h-full object-contain"
            />
            {allImages.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous image"
                  className={`${lightboxNavButtonClass} left-2`}
                  onClick={() => navigateLightbox('prev')}>
                  <i className="pi pi-chevron-left text-base leading-none" />
                </button>
                <button
                  type="button"
                  aria-label="Next image"
                  className={`${lightboxNavButtonClass} right-2`}
                  onClick={() => navigateLightbox('next')}>
                  <i className="pi pi-chevron-right text-base leading-none" />
                </button>
              </>
            )}
          </div>

          {/* Playback controls */}
          {allImages.length > 1 && (
            <div className="mt-3 flex items-center gap-4 bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-lg">
              <Button
                icon={isPlaying ? 'pi pi-pause' : 'pi pi-play'}
                rounded
                text
                severity={isPlaying ? 'warning' : 'success'}
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  // If no image is selected, select the first one before playing
                  if (!lightboxImage && allImages.length > 0) {
                    setLightboxImage(allImages[0])
                    onSyncedImageChange?.(0)
                    setIsPlaying(true)
                  } else {
                    setIsPlaying(prev => !prev)
                  }
                }}
                tooltip={isPlaying ? 'Pause' : 'Play'}
                tooltipOptions={{ position: 'top' }}
              />
              <span className="text-sm text-gray-600 dark:text-gray-300 min-w-[80px]">
                {allImages.findIndex(img => img.fullPath === lightboxImage.fullPath) + 1} /{' '}
                {allImages.length}
              </span>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600 dark:text-gray-300">Speed:</label>
                <Dropdown
                  value={playbackSpeed}
                  options={[
                    { label: '0.5x', value: 0.5 },
                    { label: '1x', value: 1 },
                    { label: '2x', value: 2 },
                    { label: '4x', value: 4 },
                    { label: '5x', value: 5 },
                    { label: '6x', value: 6 },
                    { label: '8x', value: 8 }
                  ]}
                  onChange={e => setPlaybackSpeed(e.value)}
                  className="w-20 text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  inputId={`loop-toggle-${systemRunId}`}
                  checked={loopEnabled}
                  onChange={e => setLoopEnabled(e.checked ?? false)}
                />
                <label
                  htmlFor={`loop-toggle-${systemRunId}`}
                  className="text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                  Loop
                </label>
              </div>
              <Button
                icon="pi pi-th-large"
                text
                rounded
                severity="secondary"
                onClick={() => setLightboxImage(null)}
                tooltip="Back to grid"
                tooltipOptions={{ position: 'top' }}
                className="p-button-sm"
              />
              <Button
                label="Open in MAV"
                icon="pi pi-external-link"
                severity="info"
                size="small"
                onClick={() => handleOpenInMav(lightboxImage)}
                tooltip="Open in MAV-Aero"
                tooltipOptions={{ position: 'top' }}
                style={{ color: '#fff' }}
                pt={{
                  icon: { style: { marginRight: '6px', color: '#fff' } },
                  label: {
                    style: {
                      color: '#fff',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      lineHeight: '16px'
                    }
                  }
                }}
              />
            </div>
          )}

          {/* Image info */}
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span>{lightboxImage.name}</span>
            <span>{formatFileSize(lightboxImage.size)}</span>
            {lightboxImage.metadata.section && (
              <>
                <span>Section: {lightboxImage.metadata.section}</span>
                <span>Type: {lightboxImage.metadata.metricType}</span>
                <span>Index: {lightboxImage.metadata.index}</span>
                <span>Value: {lightboxImage.metadata.value}</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Lightbox Dialog — only used in single (non-paired) mode */}
      {!isPaired && (
        <Dialog
          visible={!!lightboxImage}
          onHide={() => setLightboxImage(null)}
          className="max-w-[95vw] max-h-[95vh]"
          header={lightboxImage?.name}
          icons={
            <Button
              label="Open in MAV"
              icon="pi pi-external-link"
              severity="info"
              size="small"
              className="mr-2"
              style={{ color: '#fff' }}
              pt={{
                icon: { style: { marginRight: '6px', color: '#fff' } },
                label: {
                  style: {
                    color: '#fff',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    lineHeight: '16px'
                  }
                }
              }}
              onClick={() => lightboxImage && handleOpenInMav(lightboxImage)}
            />
          }
          style={{ width: 'auto', maxWidth: '95vw' }}
          modal
          dismissableMask>
          {lightboxImage && (
            <div className="flex flex-col items-center">
              <div className="relative">
                <img
                  src={lightboxImage.url}
                  alt={lightboxImage.name}
                  className="max-w-full max-h-[80vh] object-contain"
                />

                {/* Navigation arrows */}
                {allImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      aria-label="Previous image"
                      className={`${lightboxNavButtonClass} left-2`}
                      onClick={() => navigateLightbox('prev')}>
                      <i className="pi pi-chevron-left text-base leading-none" />
                    </button>
                    <button
                      type="button"
                      aria-label="Next image"
                      className={`${lightboxNavButtonClass} right-2`}
                      onClick={() => navigateLightbox('next')}>
                      <i className="pi pi-chevron-right text-base leading-none" />
                    </button>
                  </>
                )}
              </div>

              {/* Playback Controls */}
              {allImages.length > 1 && (
                <div className="mt-4 flex items-center gap-4 bg-gray-100 dark:bg-gray-700 px-4 py-3 rounded-lg">
                  {/* Play/Pause button */}
                  <Button
                    icon={isPlaying ? 'pi pi-pause' : 'pi pi-play'}
                    rounded
                    text
                    severity={isPlaying ? 'warning' : 'success'}
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      // If no image is selected, this shouldn't happen in dialog mode, but just in case
                      if (!lightboxImage && allImages.length > 0) {
                        setLightboxImage(allImages[0])
                        setIsPlaying(true)
                      } else {
                        setIsPlaying(prev => !prev)
                      }
                    }}
                    tooltip={isPlaying ? 'Pause' : 'Play'}
                    tooltipOptions={{ position: 'top' }}
                  />

                  {/* Progress indicator */}
                  <span className="text-sm text-gray-600 dark:text-gray-300 min-w-[80px]">
                    {allImages.findIndex(img => img.fullPath === lightboxImage.fullPath) + 1} /{' '}
                    {allImages.length}
                  </span>

                  {/* Speed control */}
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-600 dark:text-gray-300">Speed:</label>
                    <Dropdown
                      value={playbackSpeed}
                      options={[
                        { label: '0.5x', value: 0.5 },
                        { label: '1x', value: 1 },
                        { label: '2x', value: 2 },
                        { label: '4x', value: 4 },
                        { label: '5x', value: 5 },
                        { label: '6x', value: 6 },
                        { label: '8x', value: 8 }
                      ]}
                      onChange={e => setPlaybackSpeed(e.value)}
                      className="w-20 text-sm"
                    />
                  </div>

                  {/* Loop toggle */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      inputId="loop-toggle"
                      checked={loopEnabled}
                      onChange={e => setLoopEnabled(e.checked ?? false)}
                    />
                    <label
                      htmlFor="loop-toggle"
                      className="text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                      Loop
                    </label>
                  </div>
                </div>
              )}

              {/* Image info */}
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                <span>{formatFileSize(lightboxImage.size)}</span>
                {lightboxImage.metadata.section && (
                  <>
                    <span>Section: {lightboxImage.metadata.section}</span>
                    <span>Type: {lightboxImage.metadata.metricType}</span>
                    <span>Index: {lightboxImage.metadata.index}</span>
                    <span>Value: {lightboxImage.metadata.value}</span>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="mt-4 flex gap-2">
                <Button
                  label="Back to Grid"
                  icon="pi pi-th-large"
                  severity="secondary"
                  onClick={() => setLightboxImage(null)}
                />
                <Button
                  label="Download"
                  icon="pi pi-download"
                  onClick={() => {
                    const link = document.createElement('a')
                    link.href = lightboxImage.url
                    link.download = lightboxImage.name
                    link.click()
                  }}
                />
                {comparisonMode && (
                  <Button
                    label={
                      selectedForComparison.some(img => img.fullPath === lightboxImage.fullPath)
                        ? 'Remove from Compare'
                        : 'Add to Compare'
                    }
                    icon="pi pi-copy"
                    outlined
                    onClick={() => toggleComparison(lightboxImage)}
                  />
                )}
              </div>
            </div>
          )}
        </Dialog>
      )}

      {/* Comparison Dialog */}
      <Dialog
        visible={showComparisonDialog}
        onHide={() => setShowComparisonDialog(false)}
        header="Image Comparison"
        style={{ width: '95vw', height: '95vh' }}
        maximizable
        modal>
        <div
          className={classNames('grid gap-4', {
            'grid-cols-2': selectedForComparison.length === 2,
            'grid-cols-3': selectedForComparison.length === 3,
            'grid-cols-2 grid-rows-2':
              selectedForComparison.length !== 2 && selectedForComparison.length !== 3
          })}
          style={{ height: 'calc(95vh - 150px)' }}>
          {selectedForComparison.map(image => (
            <div key={image.fullPath} className="flex flex-col items-center h-full">
              <img src={image.url} alt={image.name} className="max-w-full h-full object-contain" />
              <div className="mt-2 text-center">
                <p className="text-sm font-medium">{image.name}</p>
                {image.metadata.section && (
                  <p className="text-xs text-gray-500">
                    {image.metadata.section} | {image.metadata.metricType} | #{image.metadata.index}{' '}
                    | {image.metadata.value}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Comparison actions */}
        <div className="flex justify-center gap-4 mt-4">
          <Button
            label="Clear Selection"
            icon="pi pi-trash"
            severity="secondary"
            onClick={() => {
              setSelectedForComparison([])
              setShowComparisonDialog(false)
            }}
          />
        </div>
      </Dialog>
    </div>
  )
}

export default PostProcGallery
